import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Layers,
  Search,
  Table2,
} from 'lucide-react';
import { formatNumber } from './dataDictionaryUtils';

const rowBase =
  'flex w-full items-center gap-1.5 rounded-md px-2 h-8 text-[13px] text-left text-[#3F3F46] hover:bg-[#E8EEF5] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#18181B]/20';
const rowSelected = 'bg-primary-50 text-primary-700 font-medium hover:bg-primary-50';

function TreeRow({ depth = 0, icon: Icon, label, count, selected, expandable, expanded, onToggle, onSelect, warn }) {
  return (
    <div
      className={`${rowBase} ${selected ? rowSelected : ''} ${warn ? 'text-amber-800' : ''}`}
      style={{ paddingLeft: 8 + depth * 18 }}
      role="treeitem"
      aria-selected={selected}
      aria-expanded={expandable ? expanded : undefined}
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-zinc-400 hover:text-zinc-700 disabled:invisible"
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
        disabled={!expandable}
        aria-label={expanded ? 'Collapse' : 'Expand'}
        tabIndex={-1}
      >
        {expanded ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
      </button>
      <button type="button" className="flex min-w-0 flex-1 items-center gap-1.5" onClick={onSelect}>
        <Icon size={16} className={`shrink-0 ${selected ? 'text-primary-700' : 'text-zinc-500'}`} aria-hidden />
        <span className="truncate">{label}</span>
        <span className="ml-auto pl-2 text-xs tabular-nums text-zinc-400">{formatNumber(count)}</span>
      </button>
    </div>
  );
}

function filterFolders(nodes, needle) {
  if (!needle) return nodes;
  const n = needle.toLowerCase();
  const walk = (list) =>
    list
      .map((node) => {
        const children = walk(node.children || []);
        const match = node.name.toLowerCase().includes(n);
        if (!match && children.length === 0) return null;
        return { ...node, children, forceOpen: true };
      })
      .filter(Boolean);
  return walk(nodes);
}

function FolderNodes({ nodes, depth, scope, expanded, toggle, onSelectFolder }) {
  return nodes.map((node) => {
    const key = node.path.join(' ');
    const isExpanded = node.forceOpen || expanded.has(key);
    const selected =
      scope?.type === 'folder' && Array.isArray(scope.path) && scope.path.join(' ') === key;
    const hasChildren = (node.children || []).length > 0;
    return (
      <React.Fragment key={key}>
        <TreeRow
          depth={depth}
          icon={isExpanded && hasChildren ? FolderOpen : Folder}
          label={node.name}
          count={node.total}
          selected={selected}
          expandable={hasChildren}
          expanded={isExpanded}
          onToggle={() => toggle(key)}
          onSelect={() => {
            onSelectFolder(node.path);
            if (hasChildren && !isExpanded) toggle(key);
          }}
        />
        {hasChildren && isExpanded ? (
          <FolderNodes
            nodes={node.children}
            depth={depth + 1}
            scope={scope}
            expanded={expanded}
            toggle={toggle}
            onSelectFolder={onSelectFolder}
          />
        ) : null}
      </React.Fragment>
    );
  });
}

/**
 * Left-hand navigation for the Data Dictionary: browse by folder tree or by schema/table.
 * `scope` is {type:'all'} | {type:'folder', path:[]} | {type:'nofolder'} | {type:'schema', schema} | {type:'table', schema, table}.
 */
export default function DictionaryTree({
  mode,
  onModeChange,
  folders,
  noFolder,
  tables,
  totalColumns,
  scope,
  onScopeChange,
}) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleFolders = useMemo(() => filterFolders(folders || [], filter.trim()), [folders, filter]);

  const schemas = useMemo(() => {
    const bySchema = new Map();
    (tables || []).forEach((t) => {
      const entry = bySchema.get(t.schema) || { schema: t.schema, total: 0, tables: [] };
      entry.total += Number(t.total || 0);
      entry.tables.push(t);
      bySchema.set(t.schema, entry);
    });
    const n = filter.trim().toLowerCase();
    return Array.from(bySchema.values())
      .map((s) => ({
        ...s,
        tables: n
          ? s.tables.filter((t) => t.table_name.toLowerCase().includes(n) || s.schema.toLowerCase().includes(n))
          : s.tables,
      }))
      .filter((s) => s.tables.length > 0)
      .sort((a, b) => a.schema.localeCompare(b.schema));
  }, [tables, filter]);

  // Auto-expand ancestors of the current scope so the selection is always visible.
  const scopeAncestorKeys = useMemo(() => {
    const keys = new Set();
    if (scope?.type === 'folder' && Array.isArray(scope.path)) {
      for (let i = 1; i < scope.path.length; i += 1) keys.add(scope.path.slice(0, i).join(' '));
    }
    if (scope?.type === 'table' && scope.schema) keys.add(`schema:${scope.schema}`);
    return keys;
  }, [scope]);
  const effectiveExpanded = useMemo(() => {
    if (scopeAncestorKeys.size === 0) return expanded;
    const next = new Set(expanded);
    scopeAncestorKeys.forEach((k) => next.add(k));
    return next;
  }, [expanded, scopeAncestorKeys]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3">
      <div className="flex rounded-lg bg-[#E8EEF5] p-[3px]" role="tablist" aria-label="Browse by">
        {[
          ['folders', 'Folders'],
          ['tables', 'Tables'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => onModeChange(value)}
            className={`flex-1 rounded-md px-3 py-1 text-[13px] font-medium transition-colors ${
              mode === value ? 'bg-white text-[#18181B] shadow-sm' : 'text-[#71717A] hover:text-[#18181B]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <label className="relative block">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden />
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={mode === 'folders' ? 'Filter folders…' : 'Filter tables…'}
          aria-label={mode === 'folders' ? 'Filter folders' : 'Filter tables'}
          className="h-8 w-full rounded-md border border-hairline bg-surface pl-8 pr-2 text-[13px] placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
        />
      </label>

      <div className="min-h-0 flex-1 overflow-y-auto" role="tree">
        <div className="flex flex-col gap-px">
          <TreeRow
            icon={Layers}
            label="All columns"
            count={totalColumns}
            selected={!scope || scope.type === 'all'}
            onSelect={() => onScopeChange({ type: 'all' })}
          />
          {mode === 'folders' ? (
            <>
              <FolderNodes
                nodes={visibleFolders}
                depth={0}
                scope={scope}
                expanded={effectiveExpanded}
                toggle={toggle}
                onSelectFolder={(path) => onScopeChange({ type: 'folder', path })}
              />
              {visibleFolders.length === 0 && filter.trim() ? (
                <p className="px-2 py-3 text-xs text-zinc-500">No folders match “{filter.trim()}”.</p>
              ) : null}
              {noFolder?.total > 0 && !filter.trim() ? (
                <div className="mt-2 border-t border-hairline pt-2">
                  <TreeRow
                    icon={AlertTriangle}
                    label="No folder"
                    count={noFolder.total}
                    selected={scope?.type === 'nofolder'}
                    warn
                    onSelect={() => onScopeChange({ type: 'nofolder' })}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <>
              {schemas.map((s) => {
                const key = `schema:${s.schema}`;
                const isExpanded = effectiveExpanded.has(key) || Boolean(filter.trim());
                return (
                  <React.Fragment key={key}>
                    <TreeRow
                      icon={isExpanded ? FolderOpen : Folder}
                      label={s.schema}
                      count={s.total}
                      selected={scope?.type === 'schema' && scope.schema === s.schema}
                      expandable
                      expanded={isExpanded}
                      onToggle={() => toggle(key)}
                      onSelect={() => {
                        onScopeChange({ type: 'schema', schema: s.schema });
                        if (!isExpanded) toggle(key);
                      }}
                    />
                    {isExpanded
                      ? s.tables
                          .slice()
                          .sort((a, b) => a.table_name.localeCompare(b.table_name))
                          .map((t) => (
                            <TreeRow
                              key={`${s.schema}.${t.table_name}`}
                              depth={1}
                              icon={Table2}
                              label={t.table_name}
                              count={t.total}
                              selected={
                                scope?.type === 'table' && scope.schema === s.schema && scope.table === t.table_name
                              }
                              onSelect={() => onScopeChange({ type: 'table', schema: s.schema, table: t.table_name })}
                            />
                          ))
                      : null}
                  </React.Fragment>
                );
              })}
              {schemas.length === 0 && filter.trim() ? (
                <p className="px-2 py-3 text-xs text-zinc-500">No tables match “{filter.trim()}”.</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

